// Knowledge Base v5.1 - English
export const KB_CONTENT_EN = `# AIRLINE OPS: KNOWLEDGE BASE
## Player Guide v5.1

---

# WELCOME

Airline Ops is an airline management simulator where you build routes, grow your fleet, expand to new hubs, and work toward Elite Operator Status.

**No timers. No energy. No idle bullshit.**

Just you, your airline, and the challenge of building something great.

---

# TABLE OF CONTENTS

1. Getting Started
2. Your First Day
3. Building Routes
4. Growing Your Fleet
5. Route Demand
6. Reputation & Tiers
7. Relations & Hub Invites
8. Hub Auctions
9. The Hub Store
10. Aircraft Maintenance
11. Weather
12. Time Compression
13. Wings
14. Elite Operator Status (EOS)
15. The Gold A380
16. Collectible Aircraft
17. The Mythic
18. Tips & Strategy

---

# GETTING STARTED

## Your Starting Position

You begin with:
- **$40,000,000** in cash
- **2,000,000 gallons** of fuel (full tank)
- **Zero aircraft** — you need to buy them
- **KPDX (Portland International)** as your hub
- Access to **4,878 airports worldwide**

## What You Need To Do

1. **Buy aircraft** — Start with affordable regional planes
2. **Create routes** — Connect your hub to other airports
3. **Fly flights** — Click DEPART ALL to launch everything
4. **Make money** — Revenue comes from completed flights
5. **Expand** — More aircraft, more routes, more hubs
6. **Reach Elite Operator Status** — The ultimate goal

---

# YOUR FIRST DAY

## Step 1: Buy Your First Aircraft

Go to the **Aircraft Market** and buy your first planes. You have $40M — spend it wisely.

## Step 2: Create Routes

Go to **Routes** and click **+ New Route**.

Pick a destination from your hub (KPDX). Nearby airports are good starters:
- KSEA (Seattle) — 130nm
- KBOI (Boise) — 298nm
- KRDM (Redmond) — 101nm

Assign an aircraft to the route.

## Step 3: Launch Flights

Click **DEPART ALL** to launch every ready aircraft.

Watch them fly on the map. When they land, they'll automatically turn around and fly back. Revenue accumulates with each completed flight.

---

# BUILDING ROUTES

## The Routes Panel

Each route shows:
- **Origin → Destination** (e.g., KPDX → KSEA)
- **Distance** in nautical miles
- **Aircraft assigned** and their status
- **Demand indicator** (colored dot)
- **Revenue** estimate

## One Card Per Route

If you have 4 aircraft flying KPDX → KSEA, you see ONE card showing all 4, not 4 separate cards.

Click **LAUNCH ALL** on a route card to dispatch all ready aircraft on that route.

## Creating New Routes

1. Click **+ New Route**
2. Select origin (your hub or another hub you own)
3. Select destination (any of 4,878 airports)
4. Assign an aircraft
5. The route is created and ready to fly

---

# GROWING YOUR FLEET

## Aircraft Are Purchases Only

There's no leasing. Every aircraft you operate, you own. This means:
- Big upfront costs
- Real financial decisions
- Your fleet is an asset

## Fleet Size Matters

More aircraft = more routes = more revenue = faster growth.

But don't overextend. Keep cash reserves for:
- Maintenance (A-Checks)
- Hub auction bids
- Unexpected opportunities

## Upgrading Your Fleet

As you grow, graduate to larger aircraft:
- **Regional** (E175, CRJ-900) — 70-90 passengers
- **Narrowbody** (737, A320) — 150-180 passengers
- **Widebody** (787, A350) — 250-350 passengers
- **Jumbo** (747, A380) — 400+ passengers

Bigger planes on longer routes = bigger revenue per flight.

---

# ROUTE DEMAND

## How Demand Works

Every route has a **demand level** from 0-100%.

- **High demand (80-100%)**: Full revenue
- **Medium demand (50-79%)**: Reduced revenue
- **Low demand (30-49%)**: Significantly reduced revenue
- **Depleted (below 30%)**: Minimum revenue

## Demand Depletes With Use

Each completed flight reduces demand by 1.5%.

If you hammer one route with 10 aircraft, demand drops fast.

## Demand Recovers Over Time

Each game day, demand recovers by 3% (up to 100%).

Let overworked routes rest, and they'll bounce back.

## Visual Indicator

Route lines on the map change color based on demand:
- 🟢 **Green** — High demand, full revenue
- 🟡 **Yellow** — Medium demand
- 🟠 **Orange** — Low demand
- 🔴 **Red** — Depleted, poor revenue

**Strategy**: Diversify your routes. Don't put all your aircraft on one route.

---

# REPUTATION & TIERS

## What Is Reputation?

Reputation is your airline's global standing. It affects how passengers perceive you and what opportunities you unlock.

## The 5 Tiers

| Tier | What It Means |
|------|---------------|
| Tier 1 | New airline, unknown |
| Tier 2 | Established regional |
| Tier 3 | Growing network |
| Tier 4 | Major player |
| Tier 5 | Elite status eligible |

## How To Build Reputation

- **On-time performance (OTP)** — Land flights on schedule
- **Zero cancellations** — Never cancel a flight
- **Consistent service** — Keep flying, keep growing
- **Fleet size** — More aircraft shows stability

## Why Reputation Matters

- Higher tiers unlock premium pricing
- **Tier 5 is required for Elite Operator Status**

---

# RELATIONS & HUB INVITES

## What Are Relations?

Relations are your relationship with **specific airports**. Unlike reputation (which is global), relations are per-airport.

## How To Build Relations

Fly to an airport consistently. Every on-time arrival builds your relationship with that airport.

After enough flights, the airport notices you and may invite you to bid on becoming a hub there.

## Relations ≠ Reputation

You can have:
- Great global reputation but zero relations with Denver
- Strong relations with Seattle but poor global reputation

They're separate systems tracking different things.

---

# HUB AUCTIONS

## How Hub Invites Work

When you build enough relations with an airport, you may receive an **auction invite**.

This is your chance to bid for hub status at that airport.

## The Auction Process

1. **Pick a card** (1-8) — This determines your bid position, but you don't know which position you got
2. **Bids are revealed in order** — You see previous bids when it's your turn
3. **Place your bid** — Decide how much to offer
4. **Highest bid wins** — The winner gets the hub

## Auction Strategy

- If you draw an early position: You're bidding blind. Guess what others might pay.
- If you draw a late position: You see what others bid. Just beat the highest.
- The card draw is luck. The bid amount is strategy.

## AI Competitors

You're bidding against AI airlines. They have budgets — they won't bid infinitely. If you bid more than they can afford, they'll pass.

## First Hub — Two Chances

Your first hub auction is forgiving:
- If you lose, you get a second chance after 7 days
- If you lose twice, you need to earn invites by building relations

## Cooldown After Loss

If you lose an auction, you must wait 7 game days before that airport invites you again.

---

# THE HUB STORE

## Skip The Auction

Don't want to gamble on auctions? The **Hub Store** lets you purchase hubs directly.

**The catch**: You pay a 35% premium over auction prices.

| Hub | Store Price |
|-----|-------------|
| SFO | $60M |
| LAX | $60M |
| JFK | $70M |
| DEN | $70M |
| ORD | $75M |
| ATL | $75M |
| LHR | $80M |

## When To Use The Hub Store

- You keep losing auctions
- You need a specific hub NOW
- You have cash to burn
- You don't want to wait for invites

---

# AIRCRAFT MAINTENANCE

## Overview
Aircraft degrade with use and require periodic A-Check maintenance.

## Degradation Rate
- **0.2% condition loss per completed flight**
- Collectible/Anomalous aircraft: No degradation

## Condition Thresholds

| Condition | Status | Effect |
|-----------|--------|--------|
| 80-100% | Good | Normal operation |
| 60-79% | Fair | 10% delay chance per flight |
| 40-59% | Poor | 25% delay chance, -10% revenue |
| Below 40% | Grounded | Cannot dispatch until A-Check |

## A-Check Costs

| Aircraft Class | Examples | Cost |
|----------------|----------|------|
| Regional | E175, CRJ, Dash-8 | $50,000 |
| Narrowbody | 737, A320, A321 | $150,000 |
| Widebody | 777, A350, 787 | $400,000 |
| Jumbo | 747, A380 | $750,000 |

A-Check restores condition to 100% instantly.

---

# WEATHER

## Real-Time Weather

Weather affects your operations:
- **VFR** — Clear skies, normal operations
- **MVFR** — Marginal visibility, some restrictions
- **IFR** — Low visibility, delays possible
- **LIFR** — Very low visibility, significant delays

## Weather Radar

Toggle weather radar on the map to see conditions across your network.

Plan around storms. Delays hurt your on-time performance.

---

# TIME COMPRESSION

## Speed Options

Time compression lets you speed up flight animations:

**Available from start:**
| Speed | Real Time = Game Time |
|-------|----------------------|
| 1x | 1 minute = 1 minute |
| 2x | 1 minute = 2 minutes |
| 4x | 1 minute = 4 minutes |

**EOS achievement unlock:**
| Speed | Real Time = Game Time |
|-------|----------------------|
| 12x | 1 minute = 12 minutes |

Achieving Elite Operator Status permanently unlocks 12x speed. Now you can grind for collectibles at maximum speed.

## Important Note

Time compression affects **flight animation speed only**. The day clock is fixed at 10 real minutes = 1 game day, regardless of compression setting.

---

# WINGS

## What Are Wings?

Wings are a **convenience currency** that lets you rapidly expand your route network without the manual work of creating routes one by one.

## How Wings Work

When you spend **1 Wing**, the game automatically:
1. Finds the 5 nearest airports to your hubs that you don't already serve
2. Creates routes to those airports
3. Assigns available aircraft to those routes

No clicking through menus. No searching for destinations. Instant expansion.

## Getting Wings

Wings are included in **Cash Store** purchases. When you buy a cash pack, you receive Wings as a bonus alongside the in-game currency.

| Pack | Wings Included |
|------|----------------|
| Starter Pack | 1 Wing |
| Regional Pack | 3 Wings |
| Expansion Pack | 5 Wings |
| Tycoon Pack | 10 Wings |

## When To Use Wings

Wings are perfect for:
- **Rapid early expansion** — Get your network growing fast
- **Replacing stale routes** — Clear out depleted demand routes and find fresh ones
- **Lazy management** — Let the game pick routes instead of you
- **Testing new territory** — Quickly see what airports are near your hubs

## Wings vs. Manual Route Creation

| Manual Routes | Wings |
|---------------|-------|
| You pick destinations | Game picks nearest unserved |
| Full control | Convenience |
| Time-consuming | Instant |
| Free | Costs 1 Wing per 5 routes |

Wings are a shortcut, not a replacement. Strategic players will still want to handpick premium routes. Wings just make bulk expansion painless.

---

# ELITE OPERATOR STATUS (EOS)

## What Is EOS?

Elite Operator Status is the highest achievement in Airline Ops. It proves you've built and maintained a world-class operation.

EOS isn't given. It's earned.

## The Requirements

For **10 consecutive days**, you must maintain:

| Requirement | Threshold |
|-------------|-----------|
| Reputation | Tier 5 |
| On-Time Performance | 85% or higher |
| Cancellations | Zero |
| Cash Flow | Positive every day |
| Fleet Size | 30+ aircraft |

## The 10-Day Streak

This is where it gets real.

You don't just need to hit these numbers once. You need to hit them **every single day for 10 days in a row**.

- Day 1: All requirements met ✓
- Day 2: All requirements met ✓
- Day 3: All requirements met ✓
- Day 4: All requirements met ✓
- Day 5: All requirements met ✓
- Day 6: All requirements met ✓
- Day 7: All requirements met ✓
- Day 8: All requirements met ✓
- Day 9: All requirements met ✓
- Day 10: All requirements met ✓ → **EOS ACHIEVED**

## What Breaks Your Streak

**Any** of these resets your counter to zero:

- OTP drops below 85%
- A single flight cancellation
- Negative cash flow for one day
- Reputation drops below Tier 5
- Fleet size drops below 30

Day 9 and you cancel one flight? **Back to Day 1.**

Weather, mechanical issues, bad luck — anything can end your streak. That's what makes EOS meaningful.

## EOS Is Permanent

Here's the reward for the grind: **Once you earn EOS, you keep it forever.**

It can't be taken away. You proved yourself. It's yours.

## What EOS Unlocks

Achieving EOS gives you access to:

1. **The EOS Store** — An exclusive marketplace
2. **The Gold A380** — A $375,000,000 aircraft
3. **Collectible Rolls** — Each Gold A380 purchase gives you a random collectible aircraft
4. **12x Time Compression** — Maximum speed unlocked permanently

This is where the real endgame begins.

---

# THE GOLD A380

## Not A Regular A380

The Gold A380 is different from the standard A380 in the aircraft market.

- **Only available in the EOS Store**
- **Only purchasable after earning EOS**
- **Price: $375,000,000**

## Why Buy It?

Each Gold A380 purchase triggers a **collectible roll**.

You're not buying a plane. You're buying a chance at something rare.

## The Roll

When you purchase a Gold A380:
1. The game rolls against the collectible table
2. You receive a random collectible aircraft
3. The aircraft goes into your collection

$375 million sounds like a lot. It is. That's why reaching EOS and rolling for collectibles is the endgame chase.

---

# COLLECTIBLE AIRCRAFT

## 134 Collectibles

There are 134 unique collectible aircraft, divided into 5 tiers of rarity.

## The Tiers

| Tier | Drop Rate | Count | What You Get |
|------|-----------|-------|--------------|
| **Rare** | 79.5% | 53 | Premium variants, military transports |
| **Relic** | 10% | 21 | Aviation legends from history |
| **Anomalous** | 5% | 27 | Strange aircraft with unusual properties |
| **Legendary** | 5% | 32 | Engineering marvels, cancelled projects |
| **Mythic** | 0.1% | 1 | One exists. Details below. |

## Rare (79.5%)

Most of your rolls will be Rare. These are premium aircraft variants, military transports, and special editions. Solid additions to your collection, but common.

## Relic (10%)

Historic aircraft from aviation's golden age:
- Spirit of St. Louis
- Spruce Goose
- Concorde
- Wright Flyer
- And more

These are museum pieces. Owning them means something.

## Anomalous (5%)

This is where things get weird.

Anomalous aircraft don't follow normal rules. Examples:

| Aircraft | What It Does |
|----------|--------------|
| The Bermudan | Every flight redirects to Bermuda |
| Spruce Moose | Flies in your bathtub for 1 second |
| Quantum Hopper | Teleports instantly (99,999 knots) |
| Passenger Zero | Triggers a zombie apocalypse news sequence |
| Null Pointer | Causes a fake purple screen of death |
| The Perpetual | Never lands until you force it to |

These aren't just collectibles. They do things.

## Legendary (5%)

Pinnacle engineering and cancelled dreams:
- Boeing Sonic Cruiser (never built)
- 747X (proposed but cancelled)
- Boeing 2707 SST (America's supersonic that wasn't)

These represent what could have been.

## Duplicates

Roll something you already own? Duplicates can be converted into rerolls:
- 5 Rare dupes = 1 reroll
- 3 Relic dupes = 1 reroll
- 2 Anomalous dupes = 1 reroll

Nothing is wasted.

---

# THE MYTHIC

## Ghost Apotheosis (0.1%)

One aircraft. One chance in 1000.

We're not going to tell you what it looks like. We're not going to tell you what it does in the air.

What we will tell you is what happens when you roll it.

## Mythic Rewards

Rolling the Mythic triggers the biggest payout in the game:

### $3,000,000,000 Cash
Three billion dollars. Instantly. That's 8 Gold A380 rolls worth of cash.

### Half-Price Fuel (14 Days)
All fuel purchases cost 50% less for the next 14 game days.

### Double Revenue (14 Days)
All flight revenue is doubled for the next 14 game days.

### Your Name on the Leaderboard
For 30 days, your name appears with a special crown on all leaderboards. Everyone knows you did it.

### Design the Next Mythic
You get a link to a form where you can suggest ideas for next month's Mythic aircraft. Name, special ability, anything. The developers will see it.

## The Chase

0.1% means roughly 1 in 1000 rolls.

At $375M per roll, that's about $375 billion in Gold A380 purchases to statistically expect one Mythic.

Some players will get it on roll 5. Some won't see it until roll 500.

That's the chase. That's the endgame.

Good luck.

---

# TIPS & STRATEGY

## Early Game

1. **Don't overspend** — Buy 3-5 cheap aircraft, keep cash reserves
2. **Start regional** — Short routes, quick turnarounds, steady income
3. **Watch OTP** — On-time performance matters from day one
4. **Diversify routes** — Don't hammer one route into the ground

## Mid Game

1. **Build relations** — Fly consistently to airports you want as hubs
2. **Win hub auctions** — Expand your network
3. **Upgrade fleet** — Graduate to larger aircraft
4. **Monitor demand** — Keep routes green, not red

## Late Game

1. **Push for Tier 5** — You need it for EOS
2. **Maintain 85%+ OTP** — Non-negotiable
3. **Build reserves** — You need $375M per Gold A380 roll
4. **Prepare for the streak** — 10 days requires focus

## The EOS Push

When you're ready to attempt EOS:

1. **Check all requirements first** — Don't start until you're solid
2. **Build schedule buffers** — Weather will test you
3. **Never cancel** — Delay if you must, but never cancel
4. **Monitor daily** — Check your metrics every day
5. **Use 4x speed wisely** — Faster means more flights but more chances for mistakes
6. **Stay calm on Day 9** — One more day. Don't mess it up.

---

# FINAL NOTES

This is a game about building something and proving you can run it well.

There are no shortcuts. No paying to skip the hard parts. No timers holding you back.

Just you, your decisions, and the 10-day streak standing between you and Elite Operator Status.

Good luck. The sky is waiting.

---

*Knowledge Base v5.2 — January 29, 2026*
`;
